<template>
    <div class="toolbar">
        <div class="toolbar-group">
            <rich-text-area-toolbar-button
                icon="mdi-format-bold"
                tooltip="Bold"
                :active="editor.isActive('bold')"
                @click="editor.chain().focus().toggleBold().run()" />
            <rich-text-area-toolbar-button
                icon="mdi-format-italic"
                tooltip="Italic"
                :active="editor.isActive('italic')"
                @click="editor.chain().focus().toggleItalic().run()" />
            <rich-text-area-toolbar-button
                icon="mdi-format-underline"
                tooltip="Underline"
                :active="editor.isActive('underline')"
                @click="editor.chain().focus().toggleUnderline().run()" />
            <rich-text-area-toolbar-button
                icon="mdi-format-strikethrough"
                tooltip="Strikethrough"
                :active="editor.isActive('strike')"
                @click="editor.chain().focus().toggleStrike().run()" />
            <rich-text-area-toolbar-button
                icon="mdi-link"
                tooltip="Link"
                :active="editor.isActive('link')"
                @click="setLink" />
        </div>

        <span class="separator" />

        <div class="toolbar-group">
            <rich-text-area-toolbar-button
                icon="mdi-image"
                tooltip="Image"
                @click="$emit('add-image')" />
        </div>

        <span class="separator" />

        <div class="toolbar-group">
            <rich-text-area-toolbar-button
                icon="mdi-format-list-bulleted"
                tooltip="Bullet List"
                :active="editor.isActive('bulletList')"
                @click="editor.chain().focus().toggleBulletList().run()" />
            <rich-text-area-toolbar-button
                icon="mdi-format-list-numbered"
                tooltip="Numbered List"
                :active="editor.isActive('orderedList')"
                @click="editor.chain().focus().toggleOrderedList().run()" />
        </div>

        <span class="separator" />

        <div class="toolbar-group">
            <v-select
                :items="fontSizeItems"
                :value="currentFontSize"
                dense
                hide-details
                style="max-width: 7.5rem"
                label="Size"
                @input="onChangeFontSize" />
            <rich-text-area-toolbar-button
                icon="mdi-format-subscript"
                tooltip="Subscript"
                :active="editor.isActive('subscript')"
                @click="editor.chain().focus().toggleSubscript().run()" />
            <rich-text-area-toolbar-button
                icon="mdi-format-superscript"
                tooltip="Superscript"
                :active="editor.isActive('superscript')"
                @click="editor.chain().focus().toggleSuperscript().run()" />
        </div>

        <span class="separator" />

        <div class="toolbar-group">
            <rich-text-area-toolbar-button
                icon="mdi-format-align-left"
                tooltip="Align Left"
                :active="editor.isActive({ textAlign: 'left' })"
                @click="editor.chain().focus().setTextAlign('left').run()" />
            <rich-text-area-toolbar-button
                icon="mdi-format-align-center"
                tooltip="Align Center"
                :active="editor.isActive({ textAlign: 'center' })"
                @click="editor.chain().focus().setTextAlign('center').run()" />
            <rich-text-area-toolbar-button
                icon="mdi-format-align-right"
                tooltip="Align Right"
                :active="editor.isActive({ textAlign: 'right' })"
                @click="editor.chain().focus().setTextAlign('right').run()" />
            <rich-text-area-toolbar-button
                icon="mdi-format-align-justify"
                tooltip="Justify"
                :active="editor.isActive({ textAlign: 'justify' })"
                @click="editor.chain().focus().setTextAlign('justify').run()" />
        </div>

        <span class="separator" />

        <div class="toolbar-group">
            <rich-text-area-color-picker
                icon="mdi-format-color-text"
                tooltip="Text Color"
                :value="editor.getAttributes('textStyle').color || '#000000'"
                :default-color="'#000000'"
                @input="color => editor.chain().focus().setColor(color).run()" />
            <rich-text-area-color-picker
                icon="mdi-format-color-highlight"
                tooltip="Highlight Color"
                :value="editor.getAttributes('highlight').color || '#ffff00'"
                :default-color="'#ffff00'"
                @input="color => editor.chain().focus().toggleHighlight({ color }).run()" />
            <rich-text-area-toolbar-button
                icon="mdi-format-clear"
                tooltip="Clear Colors"
                @click="editor.chain().focus().unsetColor().unsetHighlight().run()" />
        </div>

        <span class="separator" />

        <div class="toolbar-group">
            <rich-text-area-toolbar-button
                icon="mdi-table-plus"
                tooltip="Insert Table"
                @click="editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: false }).run()" />
            <rich-text-area-toolbar-button
                icon="mdi-table-column-plus-before"
                tooltip="Add Column Before"
                :disabled="!editor.can().addColumnBefore()"
                @click="editor.chain().focus().addColumnBefore().run()" />
            <rich-text-area-toolbar-button
                icon="mdi-table-column-plus-after"
                tooltip="Add Column After"
                :disabled="!editor.can().addColumnAfter()"
                @click="editor.chain().focus().addColumnAfter().run()" />
            <rich-text-area-toolbar-button
                icon="mdi-table-column-remove"
                tooltip="Delete Column"
                :disabled="!editor.can().deleteColumn()"
                @click="editor.chain().focus().deleteColumn().run()" />
            <rich-text-area-toolbar-button
                icon="mdi-table-row-plus-before"
                tooltip="Add Row Before"
                :disabled="!editor.can().addRowBefore()"
                @click="editor.chain().focus().addRowBefore().run()" />
            <rich-text-area-toolbar-button
                icon="mdi-table-row-plus-after"
                tooltip="Add Row After"
                :disabled="!editor.can().addRowAfter()"
                @click="editor.chain().focus().addRowAfter().run()" />
            <rich-text-area-toolbar-button
                icon="mdi-table-row-remove"
                tooltip="Delete Row"
                :disabled="!editor.can().deleteRow()"
                @click="editor.chain().focus().deleteRow().run()" />
            <rich-text-area-toolbar-button
                icon="mdi-table-remove"
                tooltip="Delete Table"
                :disabled="!editor.can().deleteTable()"
                @click="editor.chain().focus().deleteTable().run()" />
            <rich-text-area-color-picker
                icon="mdi-border-all-variant"
                tooltip="Table Border Color"
                :value="currentBorderColor"
                :disabled="!isInTable"
                @input="color => $emit('set-table-border-color', color)" />
            <rich-text-area-color-picker
                icon="mdi-format-color-fill"
                tooltip="Cell Background Color"
                :value="currentCellBackground"
                :disabled="!isInTableCell"
                @input="color => $emit('set-cell-background', color)" />
            <rich-text-area-toolbar-button
                icon="mdi-format-color-fill"
                tooltip="Clear Cell Background"
                :disabled="!isInTableCell"
                @click="$emit('set-cell-background', null)" />
        </div>
        <rich-text-area-help />
    </div>
</template>

<script>
import RichTextAreaToolbarButton from '@/components/richTextArea/RichTextAreaToolbarButton.vue'
import RichTextAreaColorPicker from '@/components/richTextArea/RichTextAreaColorPicker.vue'
import RichTextAreaHelp from '@/components/richTextArea/RichTextAreaHelp.vue'

export default {
    name: 'RichTextAreaToolbar',
    components: { RichTextAreaToolbarButton, RichTextAreaColorPicker, RichTextAreaHelp },
    props: {
        editor: { type: Object, required: true },
        isInTableCell: { type: Boolean, default: false },
        isInTable: { type: Boolean, default: false },
        currentCellBackground: { type: String, default: '#ffffff' },
        currentBorderColor: { type: String, default: '#ced4da' }
    },
    data() {
        return {
            fontSizeItems: [
                { text: 'Default', value: null },
                { text: '8', value: '0.5rem' },
                { text: '9', value: '0.5625rem' },
                { text: '10', value: '0.625rem' },
                { text: '11', value: '0.6875rem' },
                { text: '12', value: '0.75rem' },
                { text: '14', value: '0.875rem' },
                { text: '16', value: '1rem' },
                { text: '18', value: '1.125rem' },
                { text: '20', value: '1.25rem' },
                { text: '22', value: '1.375rem' },
                { text: '24', value: '1.5rem' },
                { text: '26', value: '1.625rem' },
                { text: '28', value: '1.75rem' },
                { text: '36', value: '2.25rem' },
                { text: '48', value: '3rem' },
                { text: '72', value: '4.5rem' },
            ]
        }
    },
    computed: {
        currentFontSize() {
            const size = this.editor.getAttributes('fontSize').fontSize
            return size || null
        }
    },
    methods: {
        setLink() {
            const previousUrl = this.editor.getAttributes('link').href
            const url = window.prompt('URL', previousUrl)
            if (url === null) return
            if (url === '') {
                this.editor.chain().focus().extendMarkRange('link').unsetLink().run()
                return
            }
            this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
        },
        onChangeFontSize(size) {
            if (!size) {
                this.editor.chain().focus().unsetMark('fontSize').run()
                return
            }
            this.editor.chain().focus().setMark('fontSize', { fontSize: size }).run()
        }
    }
}
</script>

<style scoped>
.toolbar {
    display: flex;
    gap: 0.5rem;
    padding: 0.75rem;
    border-bottom: 0.0625rem solid #e0e0e0;
    background: linear-gradient(to bottom, #fafafa, #f5f5f5);
    flex-wrap: wrap;
    align-items: center;
}

.toolbar-group {
    display: flex;
    gap: 0.25rem;
    align-items: center;
}

.separator {
    width: 0.0625rem;
    height: 1.75rem;
    background-color: #d0d0d0;
    margin: 0 0.25rem;
}
</style>
